import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft, Lock } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import {
  answerScore, taskScorePercent, missingRequired,
  fmtPercent, relationLabel, SCALE_MIN, SCALE_MAX,
} from '../../lib/evaluation.js'

// Renders one questionnaire from its frozen form_snapshot and walks it to submission.
//
// Two things worth knowing before editing this:
//   * The paper rendered here is `task.form` — the snapshot taken at dispatch, NOT the live
//     form. That is the point: HR can reword a form mid-round without changing a paper
//     someone is halfway through.
//   * The score shown while filling in is a PREVIEW computed by src/lib/evaluation.js. The
//     real one is written by Xano at submit. They use the same normalisation; if they ever
//     disagree, the server is right.
//
// Autosave is a full replace of the answer array on a 1.2s debounce. The array is small and
// a full replace is idempotent, so a dropped request can never leave a half-written paper.

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

export default function EvaluationRunner({ task, onBack, onSubmitted }) {
  const questions = useMemo(() => task?.form?.questions || [], [task])
  const [answers, setAnswers] = useState(() => task?.answers || [])
  const [comment, setComment] = useState(task?.comment || '')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [err, setErr] = useState('')
  const [confirmSend, setConfirmSend] = useState(false)
  const [sending, setSending] = useState(false)

  const readOnly = !!task?.read_only
  const timer = useRef(null)
  const dirty = useRef(false)

  const byId = useMemo(() => new Map(answers.map((a) => [a.question_id, a])), [answers])
  const preview = taskScorePercent(questions, answers)
  const missing = missingRequired(questions, answers)

  function setAnswer(qid, patch) {
    if (readOnly) return
    dirty.current = true
    setAnswers((prev) => {
      const i = prev.findIndex((a) => a.question_id === qid)
      const next = [...prev]
      const base = i >= 0 ? next[i] : { question_id: qid, value: null, na: false }
      const merged = { ...base, ...patch }
      if (i >= 0) next[i] = merged
      else next.push(merged)
      return next
    })
  }

  // Debounced autosave.
  useEffect(() => {
    if (readOnly || !dirty.current) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setSaving(true)
      setErr('')
      try {
        await postJson(`/api/me/evaluation/task/${task.id}/answer`, { answers, comment })
        setSavedAt(new Date())
        dirty.current = false
      } catch (e) {
        setErr(e?.message || 'บันทึกอัตโนมัติไม่สำเร็จ')
      } finally {
        setSaving(false)
      }
    }, 1200)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [answers, comment, readOnly, task?.id])

  async function submit() {
    setConfirmSend(false)
    setSending(true)
    setErr('')
    try {
      const res = await postJson(`/api/me/evaluation/task/${task.id}/submit`, { answers, comment })
      onSubmitted?.(res)
    } catch (e) {
      setErr(e?.message || 'ส่งแบบประเมินไม่สำเร็จ')
    } finally {
      setSending(false)
    }
  }

  // One flat list. Sections are no longer authored — grouping half-filled headings read
  // worse than a plain numbered run of questions — but a form written before that change
  // still carries them, so they are used as headings when present and ignored otherwise.
  const sections = useMemo(() => {
    const out = []
    for (const q of questions) {
      const name = q.section || ''
      let s = out.find((x) => x.name === name)
      if (!s) { s = { name, items: [] }; out.push(s) }
      s.items.push(q)
    }
    return out
  }, [questions])

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-2"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            กลับไปรายการ
          </button>
          <h2 className="text-[18px] font-semibold text-slate-900 tracking-tight">
            {task?.form?.title || 'แบบประเมิน'}
          </h2>
          <p className="text-[13px] text-slate-500 mt-0.5">
            ประเมิน <span className="font-medium text-slate-700">{task?.subject?.nickname || task?.subject?.name}</span>
            {task?.subject?.position ? ` · ${task.subject.position}` : ''}
            {' · '}ในฐานะ{relationLabel(task?.relation)}
          </p>
          {task?.form?.description && (
            <p className="text-[13px] text-slate-500 mt-2 max-w-2xl">{task.form.description}</p>
          )}
        </div>

        <div className="text-right flex-none">
          <div className="text-[11.5px] text-slate-400 mb-0.5">คะแนนขณะนี้</div>
          <div className="text-[22px] font-semibold text-slate-900 tabular-nums">{fmtPercent(preview)}</div>
          {!readOnly && (
            <div className="text-[11.5px] text-slate-400 mt-1 h-4">
              {saving
                ? <span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />กำลังบันทึก…</span>
                : savedAt ? `บันทึกแล้ว ${savedAt.toLocaleTimeString('th-TH')}` : ''}
            </div>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="flex items-center gap-2 text-[13px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 mb-5">
          <Lock size={14} strokeWidth={1.75} className="flex-none" />
          {task?.status === 'submitted'
            ? 'ส่งแบบประเมินนี้ไปแล้ว ดูได้อย่างเดียว'
            : 'รอบประเมินนี้ปิดแล้ว ดูได้อย่างเดียว'}
        </div>
      )}

      {err && (
        <div className="flex items-start gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5 mb-5">
          <AlertCircle size={14} strokeWidth={1.75} className="mt-0.5 flex-none" />
          {err}
        </div>
      )}

      <div className="space-y-6">
        {sections.map((sec) => (
          <div key={sec.name || '_'} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {sec.name && (
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60">
                <h3 className="text-[13.5px] font-semibold text-slate-700">{sec.name}</h3>
              </div>
            )}
            <div className="divide-y divide-slate-50">
              {sec.items.map((q) => (
                <QuestionRow
                  key={q.id}
                  q={q}
                  answer={byId.get(q.id)}
                  readOnly={readOnly}
                  onChange={(patch) => setAnswer(q.id, patch)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-6">
        <label className="block text-[12.5px] font-medium text-slate-500 mb-1">
          ความเห็นเพิ่มเติม (ไม่คิดคะแนน)
        </label>
        <textarea
          className={`${FIELD} min-h-[90px]`}
          value={comment}
          disabled={readOnly}
          placeholder="สิ่งที่อยากให้ทำต่อ และสิ่งที่อยากให้ปรับ"
          onChange={(e) => { dirty.current = true; setComment(e.target.value) }}
        />
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between gap-4 mt-6">
          <div className="text-[13px] text-slate-500">
            {missing.length > 0
              ? <span className="text-amber-600">ยังไม่ได้ตอบ {missing.length} ข้อที่จำเป็น</span>
              : 'ตอบครบแล้ว พร้อมส่ง'}
          </div>
          <button
            type="button"
            disabled={missing.length > 0 || sending}
            onClick={() => setConfirmSend(true)}
            className="px-4 py-2.5 rounded-xl text-[14px] font-semibold text-white bg-brand-600 hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} strokeWidth={1.75} />}
            ส่งแบบประเมิน
          </button>
        </div>
      )}

      {confirmSend && (
        <ConfirmDialog
          title="ส่งแบบประเมิน?"
          message="เมื่อส่งแล้วจะแก้ไขไม่ได้ และคะแนนจะถูกนำไปรวมทันที"
          confirmLabel="ส่งเลย"
          onConfirm={submit}
          onCancel={() => setConfirmSend(false)}
        />
      )}
    </div>
  )
}

function QuestionRow({ q, answer, readOnly, onChange }) {
  const s = answerScore(q, answer)
  const na = answer?.na === true

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 mb-2.5">
        <div className="text-[13.5px] text-slate-800 leading-relaxed">
          {q.label}
          {q.required && <span className="text-rose-400 ml-1">*</span>}
          {q.type !== 'text' && Number(q.weight ?? 1) !== 1 && (
            <span className="ml-2 text-[11.5px] text-slate-400">น้ำหนัก ×{q.weight}</span>
          )}
        </div>
        {q.type !== 'text' && s != null && (
          <span className="text-[11.5px] text-slate-400 tabular-nums flex-none">{Math.round(s * 100)}%</span>
        )}
      </div>

      {q.type === 'scale' && (
        <ScaleInput q={q} value={answer?.value} disabled={readOnly || na} onPick={(v) => onChange({ value: v, na: false })} />
      )}

      {q.type === 'dropdown' && (
        <div className="space-y-1.5">
          {(q.options || []).map((o) => (
            <label
              key={o.value}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                answer?.value === o.value && !na
                  ? 'border-brand-400 bg-brand-50/50'
                  : 'border-slate-200 hover:bg-slate-50'
              } ${(readOnly || na) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                className="accent-brand-600"
                disabled={readOnly || na}
                checked={answer?.value === o.value && !na}
                onChange={() => onChange({ value: o.value, na: false })}
              />
              <span className="text-[13px] text-slate-700">{o.label}</span>
            </label>
          ))}
        </div>
      )}

      {q.type === 'yesno' && (
        <div className="flex gap-2">
          {[{ v: 'yes', label: 'ใช่' }, { v: 'no', label: 'ไม่ใช่' }].map((o) => (
            <button
              key={o.v}
              type="button"
              disabled={readOnly || na}
              onClick={() => onChange({ value: o.v, na: false })}
              className={`px-4 py-2 rounded-xl text-[13px] font-medium border transition-colors disabled:opacity-50 ${
                answer?.value === o.v && !na
                  ? 'border-brand-400 bg-brand-50 text-brand-700'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}

      {q.type === 'text' && (
        <textarea
          className={`${FIELD} min-h-[70px]`}
          disabled={readOnly}
          value={answer?.value || ''}
          placeholder="เขียนสิ่งที่เห็นจริง ไม่ต้องสรุปเป็นความรู้สึกรวม"
          onChange={(e) => onChange({ value: e.target.value, na: false })}
        />
      )}

      {q.allow_na && q.type !== 'text' && (
        <label className="inline-flex items-center gap-1.5 mt-2.5 text-[12.5px] text-slate-500 cursor-pointer">
          <input
            type="checkbox"
            className="accent-slate-400"
            disabled={readOnly}
            checked={na}
            onChange={(e) => onChange({ na: e.target.checked, value: e.target.checked ? null : answer?.value ?? null })}
          />
          ไม่มีข้อมูล / ไม่เกี่ยวข้อง
          <span className="text-slate-400">(ไม่นำมาคิดคะแนน)</span>
        </label>
      )}
    </div>
  )
}

// Plain 1-5. No per-level wording by design — a scale question is answered on the number,
// and anything that genuinely needs each option spelled out should be a `dropdown` instead,
// where the wording carries an explicit score. min/max are still read from the question so
// forms authored before the scale was fixed keep rendering correctly.
function ScaleInput({ q, value, disabled, onPick }) {
  const lo = Number(q.scale?.min ?? SCALE_MIN)
  const hi = Number(q.scale?.max ?? SCALE_MAX)
  const steps = []
  for (let i = lo; i <= hi; i += 1) steps.push(i)

  return (
    <div className="flex gap-1.5 flex-wrap">
      {steps.map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onPick(n)}
          className={`min-w-[42px] px-3 py-2 rounded-xl text-[13px] font-medium border transition-colors disabled:opacity-50 ${
            Number(value) === n
              ? 'border-brand-400 bg-brand-50 text-brand-700'
              : 'border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  )
}
